import { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LabeledSwitch from "@/common/components/LabeledSwitch";
import { useAccountContext } from "@/state/providers/AccountProvider";
import {
    ClinicChangedError,
    CornerstoneAutoFinalize,
    cornerstoneAutoFinalizeKey,
    saveCornerstoneAutoFinalize,
} from "@common/lib/api/cornerstoneSettings";
import { classifyNetworkError } from "@common/lib/sentryUtils";
import { AUTO_FINALIZE_MAX, AUTO_FINALIZE_MIN, initialDays, parseDays, stepDays } from "./autoFinalizeDays";
import { useCornerstoneAutoFinalize } from "./useCornerstoneAutoFinalize";

const STEP_DEBOUNCE_MS = 500;

type SaveVars = { setting: CornerstoneAutoFinalize; token: number; accountId: string };

const sameSetting = (a: CornerstoneAutoFinalize, b: CornerstoneAutoFinalize) =>
    a.enabled === b.enabled && a.days === b.days;

/** Defensive only: the DB CHECK (enabled_needs_days) makes "on with no days" impossible. If it ever arrives, show it as off, matching the export. */
const asShown = (s: CornerstoneAutoFinalize): CornerstoneAutoFinalize =>
    s.enabled && s.days === null ? { enabled: false, days: null } : s;

const Chevron = ({ d }: { d: string }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const STEP_BUTTON =
    "h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-max-900";

/**
 * EN-1320: auto-finalize setting on the Cornerstone card. Renders nothing until the setting has loaded;
 * ConnectedIntegration only mounts it for a HappyBridge Cornerstone clinic whose query returned data.
 */
const CornerstoneAutoFinalizeRow = () => {
    const { data } = useCornerstoneAutoFinalize(true);
    if (!data) return null;
    return <AutoFinalizeControls saved={asShown(data)} />;
};

const AutoFinalizeControls = ({ saved }: { saved: CornerstoneAutoFinalize }) => {
    const { accountData } = useAccountContext();
    const accountId = accountData?.id;
    const queryClient = useQueryClient();
    const fieldId = useId();
    const helpId = `${fieldId}-help`;
    const errorId = `${fieldId}-error`;

    const [enabled, setEnabled] = useState(saved.enabled);
    const [daysInput, setDaysInputState] = useState(String(initialDays(saved.days)));
    const [invalid, setInvalid] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    // Mirrors `daysInput` so the debounce timer and blur read the live value, not a stale closure.
    const daysInputRef = useRef(daysInput);
    // Last server-confirmed setting: the revert target when a save fails.
    const committedRef = useRef(saved);
    // Last setting dispatched (or committed). Used to skip redundant saves and to recognise our own cache writes.
    const requestedRef = useRef(saved);
    // Ordinal of the most recent dispatch. Only the latest save may write the cache, revert, or show the inline
    // error, so an older, slower response never overwrites newer state (same idea as ConnectedIntegration's
    // alert color). apiFetch reports every failed request to Sentry itself.
    const latestTokenRef = useRef(0);
    const pendingSavesRef = useRef(0);
    // True from the first keystroke or arrow tap until that edit is saved, or replaced by a toggle or an external
    // setting. An invalid value stays dirty, so a refetch never overwrites what the user typed.
    const dirtyRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // False once the row has closed. There is no inline message then, so onError toasts the failures apiFetch
    // does not, and an invalid unsaved value gets a toast instead of the field error.
    const mountedRef = useRef(true);
    // The clinic this row is showing, for callbacks that outlive the render that created them.
    const accountIdRef = useRef(accountId);
    accountIdRef.current = accountId;

    const setDaysInput = (value: string) => {
        daysInputRef.current = value;
        setDaysInputState(value);
    };

    const showSetting = (s: CornerstoneAutoFinalize) => {
        setEnabled(s.enabled);
        setDaysInput(String(initialDays(s.days)));
        setInvalid(false);
        dirtyRef.current = false;
    };

    /** Treat `s` as the confirmed server state: revert target, dedupe baseline, and what the field shows. */
    const adoptSetting = (s: CornerstoneAutoFinalize) => {
        committedRef.current = s;
        requestedRef.current = s;
        showSetting(s);
    };

    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const mutation = useMutation({
        // Sent only if the selected clinic is still the one the save was made on (see saveCornerstoneAutoFinalize).
        mutationFn: ({ setting, accountId: savedFor }: SaveVars) => saveCornerstoneAutoFinalize(setting, savedFor),
        // One scope: saves reach the server in dispatch order, so the last one dispatched is the last one applied.
        scope: { id: "cornerstone-auto-finalize" },
        // A GET already in flight could land after this save with the old value; cancel it.
        onMutate: ({ accountId: savedFor }) =>
            queryClient.cancelQueries({ queryKey: cornerstoneAutoFinalizeKey(savedFor) }),
        onSuccess: (result, { token, accountId: savedFor }) => {
            committedRef.current = result;
            if (token === latestTokenRef.current) {
                queryClient.setQueryData(cornerstoneAutoFinalizeKey(savedFor), result);
            }
        },
        onError: (error, { token, accountId: savedFor }) => {
            // Dropped because another clinic is selected: nothing was sent. If the row has closed or now shows
            // another clinic (it is keyed by clinic and remounts), there is nothing to revert. If it still shows
            // the clinic the save was made on (another tab switched clinics), revert and say so like any failure.
            if (error instanceof ClinicChangedError && (!mountedRef.current || accountIdRef.current !== savedFor)) {
                return;
            }
            if (token !== latestTokenRef.current) return;
            // The row closed before this failed, so the inline message is gone. apiFetch usually toasts network
            // and timeout errors itself (not when the client was interrupted, e.g. asleep or offline, EN-1224);
            // anything else would otherwise fail silently.
            if (!mountedRef.current && classifyNetworkError(error) === "code_bug") {
                toast.error("Couldn't save the auto-finalize setting. Open the Cornerstone settings to try again.");
            }
            // Keep an edit the user has started since this save went out; otherwise show the last saved value.
            if (dirtyRef.current) requestedRef.current = committedRef.current;
            else adoptSetting(committedRef.current);
            setSaveFailed(true);
            queryClient.setQueryData(cornerstoneAutoFinalizeKey(savedFor), committedRef.current);
        },
        onSettled: () => {
            pendingSavesRef.current -= 1;
        },
    });

    const save = (setting: CornerstoneAutoFinalize) => {
        if (!accountId) return;
        if (sameSetting(setting, requestedRef.current)) return;
        requestedRef.current = setting;
        const token = ++latestTokenRef.current;
        pendingSavesRef.current += 1;
        setSaveFailed(false);
        mutation.mutate({ setting, token, accountId });
    };

    /** Save the field's value if the user changed it. Invalid values are flagged and never sent. */
    const flushDays = () => {
        clearTimer();
        if (!dirtyRef.current) return;
        const days = parseDays(daysInputRef.current);
        if (days === null) {
            // After the row closes there is no field to flag, so say the edit was not kept.
            if (!mountedRef.current) toast.error("Auto-finalize wasn't changed. Enter a whole number from 1 to 365.");
            setInvalid(true);
            return;
        }
        dirtyRef.current = false;
        setInvalid(false);
        save({ enabled: true, days });
    };

    // The timer and the unmount cleanup call the latest flush, so they see the current render's account id.
    const flushRef = useRef(flushDays);
    flushRef.current = flushDays;

    const handleToggle = (next: boolean) => {
        clearTimer();
        const typed = dirtyRef.current ? parseDays(daysInputRef.current) : null;
        const savedDays = requestedRef.current.days;
        // A valid typed value wins. Otherwise off keeps the last requested days (possibly null); on uses them,
        // or the default the first time.
        const days = typed ?? (next ? initialDays(savedDays) : savedDays);
        const setting = { enabled: next, days };
        showSetting(setting);
        save(setting);
    };

    const handleStep = (delta: 1 | -1) => {
        setDaysInput(String(stepDays(daysInputRef.current, delta)));
        setInvalid(false);
        dirtyRef.current = true;
        clearTimer();
        timerRef.current = setTimeout(() => flushRef.current(), STEP_DEBOUNCE_MS);
    };

    const handleChange = (value: string) => {
        clearTimer();
        setDaysInput(value);
        dirtyRef.current = true;
        if (parseDays(value) !== null) setInvalid(false);
    };

    // An unsaved edit (an arrow tap inside the debounce window, or a typed value never blurred) still goes out
    // if the card unmounts. React does not reliably fire onBlur on a focused input that is being removed.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current !== null || dirtyRef.current) flushRef.current();
        };
    }, []);

    // Adopt external changes to the cached setting (a refetch, another tab). Our own cache writes match
    // `requestedRef` and are skipped, and nothing overwrites an edit or save still in progress.
    useEffect(() => {
        if (sameSetting(saved, requestedRef.current)) return;
        if (dirtyRef.current || timerRef.current !== null || pendingSavesRef.current > 0) return;
        adoptSetting(saved);
    }, [saved]);

    const shownDays = parseDays(daysInput);

    return (
        <div className="flex flex-col gap-3 bg-white">
            <LabeledSwitch label="Auto-finalize notes in Cornerstone" checked={enabled} onChange={handleToggle} />
            {enabled ? (
                <div className="flex flex-col gap-2 pl-4 border-l-2 border-gray-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <label htmlFor={fieldId} className="text-sm font-normal leading-6 text-gray-600">
                            Finalize after
                        </label>
                        <div
                            className="flex items-center gap-2 text-sm text-gray-700"
                            onBlur={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) flushDays();
                            }}
                        >
                            <input
                                id={fieldId}
                                type="text"
                                inputMode="numeric"
                                value={daysInput}
                                onChange={(e) => handleChange(e.target.value)}
                                aria-invalid={invalid}
                                aria-describedby={invalid ? `${errorId} ${helpId}` : helpId}
                                className={classNames(
                                    "h-10 w-[72px] rounded-md border px-3 text-right text-sm text-gray-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-max-900",
                                    invalid ? "border-blaze-800 ring-1 ring-blaze-800" : "border-gray-300",
                                )}
                            />
                            <span>days</span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    aria-label="One day fewer"
                                    className={STEP_BUTTON}
                                    onClick={() => handleStep(-1)}
                                    disabled={shownDays === AUTO_FINALIZE_MIN}
                                >
                                    <Chevron d="M6 9L12 15L18 9" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="One day more"
                                    className={STEP_BUTTON}
                                    onClick={() => handleStep(1)}
                                    disabled={shownDays === AUTO_FINALIZE_MAX}
                                >
                                    <Chevron d="M18 15L12 9L6 15" />
                                </button>
                            </div>
                        </div>
                    </div>
                    {invalid && (
                        <p id={errorId} role="alert" className="text-xs text-blaze-800 text-right">
                            Enter a whole number from 1 to 365.
                        </p>
                    )}
                    <p id={helpId} className="text-sm leading-5 text-gray-600">
                        HappyDoc notes arrive in Cornerstone as Tentative and finalize after this many days. Notes
                        already in Cornerstone don't change.
                    </p>
                </div>
            ) : (
                <p className="text-sm leading-5 text-gray-600">
                    HappyDoc notes stay Tentative until someone finalizes them in Cornerstone.
                </p>
            )}
            {saveFailed && (
                <p role="alert" className="text-xs text-blaze-800">
                    Couldn't save. Try again.
                </p>
            )}
        </div>
    );
};

export default CornerstoneAutoFinalizeRow;
